import { useCallback } from 'react';

import { ExternalLinkIcon, PlusIcon, SettingsIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Navigate, useNavigate, useParams } from 'react-router';
import { useTitle } from 'react-use';

import Loading from '@/components/atoms/Loading/Loading';
import PageContentWrapper from '@/components/templates/PageContentWrapper';
import { Button } from '@/components/ui/button';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { useDeskId } from '@/hooks/useDeskId';
import useDesks from '@/hooks/useDesks';
import { selectAccountSlug } from '@/redux/session/selectors';
import useSegments from '@/segment/useSegments';
import { PageName } from '@/shared/types/segment';
import { ENVIRONMENTS_DOCS_URL } from '@/util/constants';

import ChatsHeader from './ChatsHeader';

const Chats: React.FC = () => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const { serviceDesks, desks, listStatus, handleCreateDesk } = useDesks();
  const deskId = useDeskId();
  useTitle(t('pages.chats', { 0: slug ?? '-' }));
  const { slug: urlSlug } = useParams();
  const navigate = useNavigate();

  useSegments(PageName.CHATS);

  const handleVisitSettings = useCallback(() => {
    navigate(`/${slug}/environments/${deskId}/settings`);
  }, [deskId, navigate, slug]);

  if (slug !== urlSlug || listStatus !== 'success') {
    return null;
  }

  if (desks && desks.length === 0) {
    return (
      <>
        <ChatsHeader />
        <PageContentWrapper>
          <Empty>
            <EmptyHeader>
              <EmptyMedia>
                <img src="/assets/empty-states/ai-agents.webp" alt="" />
              </EmptyMedia>
              <EmptyTitle>{t('chats.no_environment.title')}</EmptyTitle>
              <EmptyDescription>{t('chats.no_environment.description')}</EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button size="sm" onClick={handleCreateDesk}>
                <PlusIcon size={16} />
                {t('common.create')}
              </Button>

              <a
                href={ENVIRONMENTS_DOCS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:underline"
              >
                <span className="text-sm leading-6 font-medium">{t('common.learn_more')}</span>
                <ExternalLinkIcon className="h-4 w-4 text-muted-foreground" />
              </a>
            </EmptyContent>
          </Empty>
        </PageContentWrapper>
      </>
    );
  }

  // If deskId in session is a service desk , send user there , otherwise redirect to first one
  if (serviceDesks?.length > 0) {
    const isDeskService = serviceDesks?.filter((desk) => desk.desk_id === deskId).length > 0;
    return (
      <Navigate
        replace
        to={`/${slug}/chats/${isDeskService ? deskId : serviceDesks[0]?.desk_id}`}
      />
    );
  }

  return (
    <>
      {!serviceDesks && <Loading />}
      {serviceDesks?.length === 0 && (
        <>
          <ChatsHeader />
          <PageContentWrapper>
            <Empty>
              <EmptyHeader>
                <EmptyMedia>
                  <img src="/assets/empty-states/chat.webp" alt="" />
                </EmptyMedia>
                <EmptyTitle>{t('chats.no_live_chat.title')}</EmptyTitle>
                <EmptyDescription>{t('chats.no_live_chat.description')}</EmptyDescription>
              </EmptyHeader>
              <EmptyContent>
                <Button size="sm" onClick={handleVisitSettings}>
                  <SettingsIcon size={16} />
                  {t('chats.no_live_chat.button')}
                </Button>
              </EmptyContent>
            </Empty>
          </PageContentWrapper>
        </>
      )}
    </>
  );
};

export default Chats;
